import { assign, createMachine } from "xstate";
import { fetchOrders, IOrder } from "@nju/data-access-portal-order";
import { BasicError } from "@nju/result";

export type ISortOrder =
  | "orderDate"
  | "-orderDate"
  | "appointmentStartDate"
  | "-appointmentStartDate";

interface IContext {
  error?: BasicError;
  refreshInterval?: number;
  orders: IOrder[];
  page: number;
  total: number;
  perPage: number;
  paymentFilters?: string[];
  statusFilters?: string[];
  offerTypeFilters?: string[];
  cancellationReasonFilters?: string[];
  sort: ISortOrder;
}

type IEvent =
  | { type: "SET_FILTER_PAYMENT"; value?: string[] }
  | { type: "SET_FILTER_STATUS"; value?: string[] }
  | { type: "SET_FILTER_OFFER_TYPE"; value?: string[] }
  | { type: "SET_FILTER_CANCELLATION_REASON"; value?: string[] }
  | { type: "SET_CURRENT_PAGE"; value: number }
  | { type: "SET_SORT_ORDER"; value: ISortOrder }
  | {
      type: "ORDERS";
      orders?: IOrder[];
      total: number;
    }
  | { type: "ERROR"; error: BasicError };

export const dispositionsMachine = createMachine(
  {
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    context: {
      orders: [],
      total: 0,
      page: 0,
      perPage: 10,
      sort: "-orderDate",
    },
    initial: "loading",
    states: {
      ready: {
        on: {
          SET_FILTER_STATUS: {
            actions: "setFilterStatus",
            target: "loading",
          },
          SET_FILTER_PAYMENT: {
            actions: "setFilterPayment",
            target: "loading",
          },
          SET_FILTER_OFFER_TYPE: {
            actions: "setFilterOfferType",
            target: "loading",
          },
          SET_FILTER_CANCELLATION_REASON: {
            actions: "setFilterCancellationReason",
            target: "loading",
          },
          SET_CURRENT_PAGE: {
            actions: "setCurrentPage",
            target: "loading",
          },
          SET_SORT_ORDER: {
            actions: "setSortOrder",
            target: "loading",
          },
        },
      },
      loading: {
        invoke: {
          src: "loading",
        },
        on: {
          ORDERS: {
            actions: "setOrders",
            target: "ready",
          },
          ERROR: {
            actions: "setError",
            target: "error",
          },
        },
      },
      error: {
        on: {
          SET_FILTER_STATUS: {
            actions: "setFilterStatus",
            target: "loading",
          },
          SET_FILTER_PAYMENT: {
            actions: "setFilterPayment",
            target: "loading",
          },
          SET_FILTER_OFFER_TYPE: {
            actions: "setFilterOfferType",
            target: "loading",
          },
          SET_FILTER_CANCELLATION_REASON: {
            actions: "setFilterCancellationReason",
            target: "loading",
          },
        },
      },
    },
  },
  {
    actions: {
      setSortOrder: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_SORT_ORDER") {
          return {
            sort: event.value,
          };
        }
        return context;
      }),
      setFilterPayment: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_FILTER_PAYMENT") {
          return {
            paymentFilters: event.value,
            page: 0,
          };
        }
        return context;
      }),
      setFilterStatus: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_FILTER_STATUS") {
          return {
            statusFilters: event.value,
            page: 0,
          };
        }
        return context;
      }),
      setFilterOfferType: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_FILTER_OFFER_TYPE") {
          return {
            offerTypeFilters: event.value,
            page: 0,
          };
        }
        return context;
      }),
      setFilterCancellationReason: assign<IContext, IEvent>(
        (context, event) => {
          if (event.type === "SET_FILTER_CANCELLATION_REASON") {
            return {
              cancellationReasonFilters: event.value,
              page: 0,
            };
          }
          return context;
        },
      ),
      setCurrentPage: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_CURRENT_PAGE") {
          return {
            page: event.value,
          };
        }
        return context;
      }),
      setOrders: assign<IContext, IEvent>((context, event) => {
        if (event.type === "ORDERS") {
          return {
            orders: event.orders || [],
            total: event.total,
          };
        }
        return context;
      }),
      setError: assign<IContext, IEvent>((context, event) => {
        if (event.type === "ERROR") {
          return {
            error: event.error,
          };
        }
        return context;
      }),
    },
    services: {
      loading: ({
        statusFilters,
        paymentFilters,
        offerTypeFilters,
        cancellationReasonFilters,
        sort,
        page,
        perPage,
      }) => {
        return async (callback) => {
          const result = await fetchOrders({
            statusFilters: statusFilters?.join(","),
            paymentFilters: paymentFilters?.join(","),
            offerTypeFilters: offerTypeFilters?.join(","),
            cancellationReasonFilters: cancellationReasonFilters?.join(","),
            sort,
            page,
            perPage,
          });
          if (result.isOk) {
            callback({
              type: "ORDERS",
              orders: result.value.orders,
              total: result.value.total || 0,
            });
          } else {
            callback({ type: "ERROR", error: result.err });
          }
        };
      },
    },
  },
);
